import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { publicBookingService } from "../services/dataService";
import {
  BookingPage,
  BookingSlot,
  BookingSlotDay,
  GuestAppointment,
  PublicAppointmentType,
  PublicStaff,
} from "../types";
import "./PublicBooking.css";

/**
 * Girişsiz randevu sayfaları. Panelin DIŞINDA çalışır: oturum yok, menü yok,
 * yetki yok. Dört ekran aynı dosyada çünkü hepsi aynı kabuğu ve aynı hata
 * dilini paylaşıyor.
 *
 *   /randevu                     → yayında olan çalışanların dizini
 *   /randevu/:slug               → kişiye özel randevu alma
 *   /randevu/dogrula/:token      → e-posta doğrulaması
 *   /randevu/kayit/:token        → misafirin kendi randevusu
 */

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pb-page">
    <div className="pb-shell">
      <div className="pb-brand">
        <b>RANDEVU</b>
      </div>
      {children}
      <div className="pb-foot">
        Saatler Türkiye saatiyle (TSİ) gösterilir.
        <br />
        Nedaris Üretim Yönetim Sistemi ile sağlanmaktadır.
      </div>
    </div>
  </div>
);

const errText = (e: any, fallback: string) =>
  e?.response?.data?.message || e?.message || fallback;

const dayName = (d: string) =>
  new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { weekday: "short" });
const dayNum = (d: string) => new Date(d + "T00:00:00").getDate();
const monthName = (d: string) =>
  new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { month: "short" });
const longDate = (d: string) =>
  new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { dateStyle: "long" });

// ==================== Dizin ====================

export const BookingDirectory: React.FC = () => {
  const [staff, setStaff] = useState<PublicStaff[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    publicBookingService
      .staff()
      .then(setStaff)
      .catch((e) => {
        setStaff([]);
        setErr(errText(e, "Randevu sayfası şu anda kullanılamıyor."));
      });
  }, []);

  return (
    <Shell>
      <div className="pb-card">
        <h1>Randevu alın</h1>
        <p className="intro">Görüşmek istediğiniz kişiyi seçin.</p>
      </div>

      {err && <div className="pb-note bad">{err}</div>}

      {staff === null && <div className="pb-empty">Yükleniyor…</div>}
      {staff !== null && staff.length === 0 && !err && (
        <div className="pb-card">
          <div className="pb-empty">Şu anda randevuya açık bir kişi yok.</div>
        </div>
      )}

      {staff && staff.length > 0 && (
        <div className="pb-card">
          <div className="pb-opts">
            {staff.map((s) => (
              <Link key={s.slug} to={`/randevu/${s.slug}`} className="pb-opt" style={{ textDecoration: "none" }}>
                <div className="t">{s.name}</div>
                <div className="m">
                  {s.title}
                  {s.title && s.intro ? " · " : ""}
                  {s.intro}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
};

// ==================== Randevu alma ====================

export const BookingForm: React.FC = () => {
  const { slug = "" } = useParams();

  const [page, setPage] = useState<BookingPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [type, setType] = useState<PublicAppointmentType | null>(null);
  const [days, setDays] = useState<BookingSlotDay[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<BookingSlot | null>(null);
  const [mode, setMode] = useState<1 | 2>(1);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    publicBookingService
      .page(slug)
      .then(setPage)
      .catch(() => setNotFound(true));
  }, [slug]);

  // Tür seçilince o türün müsait saatleri çekilir. Sunucu kendi sınırlarını
  // uygular; buradaki 60 gün yalnız istek boyutunu tutar.
  useEffect(() => {
    if (!type) return;
    setLoadingSlots(true);
    setSlot(null);
    const from = new Date();
    const to = new Date(Date.now() + 60 * 864e5);
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    publicBookingService
      .slots(slug, type.appointmentTypeId, iso(from), iso(to))
      .then((d) => {
        setDays(d);
        setActiveDay(d[0]?.date ?? null);
      })
      .catch((e) => setErr(errText(e, "Uygun saatler alınamadı.")))
      .finally(() => setLoadingSlots(false));
  }, [type, slug]);

  useEffect(() => {
    if (!type) return;
    setMode(type.modeOption === 2 ? 2 : 1);
  }, [type]);

  const times = useMemo(
    () => days.find((d) => d.date === activeDay)?.slots ?? [],
    [days, activeDay],
  );

  const submit = async () => {
    if (!type || !slot) return;
    setErr(null);
    setSending(true);
    try {
      const r = await publicBookingService.book(slug, {
        appointmentTypeId: type.appointmentTypeId,
        startAt: slot.startAt,
        mode,
        guestName: name.trim(),
        guestEmail: email.trim(),
        guestPhone: phone.trim() || undefined,
        guestCompany: company.trim() || undefined,
        guestNote: note.trim() || undefined,
        consentAccepted: consent,
      });
      setDone(r.message);
    } catch (e: any) {
      setErr(errText(e, "Talep gönderilemedi. Lütfen tekrar deneyin."));
      // Saat kapılmış olabilir: listeyi tazele.
      if (e?.response?.status === 409 || e?.response?.data?.code === "SLOT_UNAVAILABLE") {
        setSlot(null);
        const t = type;
        setType(null);
        window.setTimeout(() => setType(t), 0);
      }
    } finally {
      setSending(false);
    }
  };

  if (notFound)
    return (
      <Shell>
        <div className="pb-card">
          <h1>Sayfa bulunamadı</h1>
          <p className="intro">
            Bu randevu bağlantısı geçerli değil ya da şu anda yayında değil. Bağlantıyı size
            gönderen kişiyle iletişime geçebilirsiniz.
          </p>
        </div>
      </Shell>
    );

  if (!page)
    return (
      <Shell>
        <div className="pb-empty">Yükleniyor…</div>
      </Shell>
    );

  if (done)
    return (
      <Shell>
        <div className="pb-card">
          <h1>Talebiniz alındı</h1>
          <div className="pb-note good" style={{ marginTop: "0.875rem" }}>
            {done}
          </div>
          <p className="intro">
            Doğrulama bağlantısına tıklamazsanız tuttuğumuz saat kısa süre sonra yeniden açılır.
            E-postayı göremiyorsanız gereksiz (spam) klasörünü kontrol edin.
          </p>
        </div>
      </Shell>
    );

  const canSubmit =
    !!type && !!slot && name.trim().length >= 2 && /\S+@\S+\.\S+/.test(email) && consent && !sending;

  return (
    <Shell>
      <div className="pb-card">
        <h1>{page.staff.name}</h1>
        {page.staff.title && <div className="role">{page.staff.title}</div>}
        {page.staff.intro && <p className="intro">{page.staff.intro}</p>}
      </div>

      {err && <div className="pb-note bad">{err}</div>}

      {/* 1 — konu */}
      <div className="pb-card">
        <div className="pb-step">
          <span className="n">1</span> Konu seçin
        </div>
        {page.types.length === 0 && (
          <div className="pb-empty">Şu anda randevuya açık bir konu yok.</div>
        )}
        <div className="pb-opts">
          {page.types.map((t) => (
            <button
              key={t.appointmentTypeId}
              className={`pb-opt ${type?.appointmentTypeId === t.appointmentTypeId ? "on" : ""}`}
              onClick={() => setType(t)}
            >
              <div className="t">{t.name}</div>
              <div className="m">
                {t.durationMinutes} dakika
                {t.description ? ` · ${t.description}` : ""}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 2 — saat */}
      {type && (
        <div className="pb-card">
          <div className="pb-step">
            <span className="n">2</span> Gün ve saat seçin
          </div>

          {loadingSlots && <div className="pb-empty">Uygun saatler aranıyor…</div>}

          {!loadingSlots && days.length === 0 && (
            <div className="pb-empty">
              Önümüzdeki dönemde uygun saat bulunamadı. Daha sonra tekrar deneyin.
            </div>
          )}

          {!loadingSlots && days.length > 0 && (
            <>
              <div className="pb-days">
                {days.map((d) => (
                  <button
                    key={d.date}
                    className={`pb-day ${activeDay === d.date ? "on" : ""}`}
                    onClick={() => {
                      setActiveDay(d.date);
                      setSlot(null);
                    }}
                  >
                    <div className="w">{dayName(d.date)}</div>
                    <div className="n">{dayNum(d.date)}</div>
                    <div className="mo">{monthName(d.date)}</div>
                  </button>
                ))}
              </div>

              <div className="pb-times">
                {times.map((t) => (
                  <button
                    key={t.startAt}
                    className={`pb-time ${slot?.startAt === t.startAt ? "on" : ""}`}
                    onClick={() => setSlot(t)}
                  >
                    {t.localTime}
                  </button>
                ))}
              </div>
              <div className="pb-zone">Saatler {page.timeZoneLabel} ile gösterilmektedir.</div>
            </>
          )}
        </div>
      )}

      {/* 3 — görüşme şekli */}
      {type && slot && type.modeOption === 3 && (
        <div className="pb-card">
          <div className="pb-step">
            <span className="n">3</span> Görüşme şekli
          </div>
          <div className="pb-opts">
            <button className={`pb-opt ${mode === 1 ? "on" : ""}`} onClick={() => setMode(1)}>
              <div className="t">Yüz yüze</div>
              <div className="m">{page.location || "Adres onay e-postasında paylaşılır."}</div>
            </button>
            <button className={`pb-opt ${mode === 2 ? "on" : ""}`} onClick={() => setMode(2)}>
              <div className="t">Çevrim içi</div>
              <div className="m">Görüşme bağlantısı onaydan sonra e-postayla gönderilir.</div>
            </button>
          </div>
        </div>
      )}

      {/* 4 — bilgiler */}
      {type && slot && (
        <div className="pb-card">
          <div className="pb-step">
            <span className="n">{type.modeOption === 3 ? 4 : 3}</span> Bilgileriniz
          </div>

          <div className="pb-summary">
            <div>
              <span>Konu</span>
              <span>{type.name}</span>
            </div>
            <div>
              <span>Tarih</span>
              <span>{longDate(activeDay!)}</span>
            </div>
            <div>
              <span>Saat</span>
              <span>
                {slot.localTime} ({page.timeZoneLabel})
              </span>
            </div>
            <div>
              <span>Görüşme</span>
              <span>{mode === 2 ? "Çevrim içi" : "Yüz yüze"}</span>
            </div>
          </div>

          <div className="pb-row2">
            <div className="pb-field">
              <label>
                Ad soyad <span className="req">*</span>
              </label>
              <input className="pb-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="pb-field">
              <label>
                E-posta <span className="req">*</span>
              </label>
              <input
                className="pb-input"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="pb-row2">
            <div className="pb-field">
              <label>Telefon</label>
              <input
                className="pb-input"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="pb-field">
              <label>Firma</label>
              <input className="pb-input" value={company} onChange={(e) => setCompany(e.target.value)} />
            </div>
          </div>

          <div className="pb-field">
            <label>Görüşmek istediğiniz konu</label>
            <textarea className="pb-textarea" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <label className="pb-consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>
              Ad, e-posta ve telefon bilgilerimin bu randevunun düzenlenmesi amacıyla işlenmesini
              kabul ediyorum. Bilgiler randevu tarihinden bir yıl sonra silinir.
            </span>
          </label>

          <div className="pb-note">
            Talebiniz {page.staff.name} tarafından onaylandığında e-posta ile bilgilendirileceksiniz.
            Önce adresinizi doğrulamanız istenecek.
          </div>

          <button className="pb-btn" onClick={submit} disabled={!canSubmit}>
            {sending ? "Gönderiliyor…" : "Randevu talebi gönder"}
          </button>
        </div>
      )}
    </Shell>
  );
};

// ==================== Doğrulama ====================

export const BookingVerify: React.FC = () => {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<"working" | "ok" | "bad">("working");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    publicBookingService
      .verify(token)
      .then((r) => {
        if (cancelled) return;
        setState("ok");
        setMsg(r.message);
        // Doğrulama tek kullanımlıktır; kullanıcıyı kendi kaydına taşı ki
        // sayfayı yenilediğinde "geçersiz bağlantı" görmesin.
        window.setTimeout(() => navigate(`/randevu/kayit/${r.token}`, { replace: true }), 2500);
      })
      .catch((e) => {
        if (cancelled) return;
        setState("bad");
        setMsg(errText(e, "Bağlantı geçersiz ya da süresi dolmuş."));
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <Shell>
      <div className="pb-card">
        <h1>{state === "bad" ? "Doğrulanamadı" : "E-posta doğrulama"}</h1>
        {state === "working" && <p className="intro">Doğrulanıyor…</p>}
        {state === "ok" && <div className="pb-note good">{msg}</div>}
        {state === "bad" && (
          <>
            <div className="pb-note bad">{msg}</div>
            <p className="intro">Yeni bir randevu talebi oluşturabilirsiniz.</p>
          </>
        )}
      </div>
    </Shell>
  );
};

// ==================== Misafirin kendi kaydı ====================

export const BookingRecord: React.FC = () => {
  const { token = "" } = useParams();
  const [apt, setApt] = useState<GuestAppointment | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    publicBookingService
      .mine(token)
      .then(setApt)
      .catch(() => setNotFound(true));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cancel = async () => {
    if (!window.confirm("Randevunuz iptal edilsin mi?")) return;
    setBusy(true);
    setErr(null);
    try {
      await publicBookingService.cancel(token, "");
      await load();
    } catch (e) {
      setErr(errText(e, "İptal edilemedi."));
    } finally {
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <Shell>
        <div className="pb-card">
          <h1>Randevu bulunamadı</h1>
          <p className="intro">Bu bağlantı geçerli değil.</p>
        </div>
      </Shell>
    );

  if (!apt)
    return (
      <Shell>
        <div className="pb-empty">Yükleniyor…</div>
      </Shell>
    );

  return (
    <Shell>
      <div className="pb-card">
        <h1>Randevunuz</h1>
        <p className="intro">
          {apt.number} · <span className={`pb-pill s${apt.status}`}>{apt.statusName}</span>
        </p>
      </div>

      {err && <div className="pb-note bad">{err}</div>}

      <div className="pb-card">
        <div className="pb-summary" style={{ marginBottom: 0 }}>
          <div>
            <span>Konu</span>
            <span>{apt.typeName}</span>
          </div>
          <div>
            <span>Görüşülecek kişi</span>
            <span>{apt.staffName}</span>
          </div>
          <div>
            <span>Tarih ve saat</span>
            <span>{apt.localWhen}</span>
          </div>
          <div>
            <span>Görüşme</span>
            <span>{apt.mode === 2 ? "Çevrim içi" : "Yüz yüze"}</span>
          </div>
          {apt.location && (
            <div>
              <span>Adres</span>
              <span>{apt.location}</span>
            </div>
          )}
        </div>

        {apt.meetingUrl && (
          <a
            className="pb-btn"
            href={apt.meetingUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display: "block", textAlign: "center", textDecoration: "none", marginTop: "1rem" }}
          >
            Görüşmeye katıl
          </a>
        )}

        {apt.canCancel && (
          <button className="pb-btn danger" style={{ marginTop: "0.625rem" }} onClick={cancel} disabled={busy}>
            {busy ? "İşleniyor…" : "Randevumu iptal et"}
          </button>
        )}
      </div>
    </Shell>
  );
};

export default BookingForm;
